import { DevelopmentError } from '../../webmodule-common/other/development-error';
import { information } from '../../webmodule-common/other/ui/modal-option';
import { noParalellExecutionAsync } from '../../webmodule-common/other/common/helpers/callbacks';
import { QuoteSupplier, V6OfflineCause } from '../quotes/quote-service';
import { LangSupplierServices } from '../../localization/scripts/v6config/supplier-services-lang';

interface GSNCache {
  supplierId: string;
  gsn: GlobalSupplierNotification;
}

export class GlobalSupplierNotification {
  public static displaySupplierOffLineMsg = noParalellExecutionAsync(displaySupplierOffLineMsg);
  public static displaySupplierUpgradingMsg = noParalellExecutionAsync(displaySupplierOffUpgrade);
  private static _instances: GSNCache[] = [];
  supplierId: any;
  public extraText: string = '';
  private _events: ((gsn: GlobalSupplierNotification) => void)[] = [];
  private _quoteSupplier?: QuoteSupplier;
  private _date?: Date;

  constructor(supplierId) {
    this.supplierId = supplierId;
  }

  private _online?: boolean;

  public get online(): boolean {
    return this._online ?? false;
  }

  private _offlineCause?: V6OfflineCause | undefined;

  public get offlineCause(): V6OfflineCause | undefined {
    return this._offlineCause;
  }

  // One notice per offline episode, shared across both routes into it: a mid-session drop via
  // makeOffline() and a cache-boot notice fired from v6config.ts's v6BootLoaderInternal via
  // notifyOfflineOnce(). Without a shared flag, a dealer who dropped mid-session got makeOffline's
  // modal, then the cache-boot notice again the next time the loader itself confirmed it was
  // booting offline (round 3 review). Cleared only by setState's own transition to online (round 4
  // review) - relying on the loader's caller to clear it left it stuck on a transient undefined, a
  // sharedExecute join, or a mid-outage live result that never took the "reset" branch.
  private _offlineNoticeShown = false;

  public get offlineNoticeShown(): boolean {
    return this._offlineNoticeShown;
  }

  public get offlineMessage(): string {
    const supplierName = this._quoteSupplier?.description ?? LangSupplierServices.supplier();
    switch (this._offlineCause) {
      case V6OfflineCause.Connection:
        return LangSupplierServices.supplierOfflineSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
      case V6OfflineCause.Upgrading:
        return LangSupplierServices.supplierOfflineUpgradingSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
      case V6OfflineCause.VersionMismatch:
        return LangSupplierServices.supplierOfflineVersionMismatch({ supplierName });
      default:
        return LangSupplierServices.supplierOfflineUnknownReasonSince({
          supplierName,
          time: this._date?.toLocaleTimeString() ?? '',
          extraText: this.extraText
        });
    }
  }

  public static getDefaultInstance(): GlobalSupplierNotification | undefined {
    if (this._instances.length > 0) return this._instances[0].gsn;
    return undefined;
  }

  public static getInstance(supplierId?: string): GlobalSupplierNotification {
    if (!supplierId) {
      if (this._instances.length > 0) return this._instances[0].gsn;
      throw new DevelopmentError('GlobalSupplierNotification missing default supplier');
    }
    let instance = this._instances.find(x => x.supplierId === supplierId);
    if (!instance) {
      instance = { supplierId: supplierId, gsn: new GlobalSupplierNotification(supplierId) };
      this._instances.push(instance);
    }
    return instance.gsn;
  }

  /**
   * Looks up an existing instance without creating one. Unlike `getInstance`, an id that doesn't
   * match anything tracked returns `undefined` rather than silently registering a new, phantom
   * supplier - for callers (the V6 boot loader) that only have an id to check against, not a real
   * reason to start tracking a supplier that isn't already known.
   */
  public static findInstance(supplierId: string): GlobalSupplierNotification | undefined {
    return this._instances.find(x => x.supplierId === supplierId)?.gsn;
  }

  public static async showOfflineMessage(offlineCause?: V6OfflineCause, offlineMessage?: string) {
    if (offlineMessage) console.log(`GlobalSupplierNotification: ${offlineMessage}`);
    if (offlineCause === V6OfflineCause.Upgrading) await GlobalSupplierNotification.displaySupplierUpgradingMsg();
    else await GlobalSupplierNotification.displaySupplierOffLineMsg();
  }

  public addEventListenter(event: (gsn: GlobalSupplierNotification) => void) {
    this._events = [...this._events.filter(x => x !== event), event];
  }

  public remEventListenter(event: (gsn: GlobalSupplierNotification) => void) {
    this._events = this._events.filter(x => x !== event);
  }

  public async makeOffline(offlineCause: V6OfflineCause, offlineMessage?: string) {
    this._offlineCause = offlineCause;
    const wasOnline = this._online;
    this._online = false;
    if (wasOnline && !this._offlineNoticeShown) {
      this._offlineNoticeShown = true;
      await this.showOfflineMessage(offlineMessage);
    }
    this._events.forEach(x => x(this));
  }

  /**
   * The cache-boot gate: shows the one-off offline notice at most once per episode, same as
   * `makeOffline`'s own gate, sharing the same `_offlineNoticeShown` flag so whichever of the two
   * routes into a notice fires first is the one the dealer actually sees.
   */
  public async notifyOfflineOnce(offlineCause: V6OfflineCause, offlineMessage?: string): Promise<void> {
    if (this._offlineNoticeShown) return;
    this._offlineNoticeShown = true;
    await GlobalSupplierNotification.showOfflineMessage(offlineCause, offlineMessage);
  }

  async showOfflineMessage(offlineMessage?: string) {
    if (offlineMessage) console.log(`GlobalSupplierNotification: ${offlineMessage}`);

    if (this._offlineCause === V6OfflineCause.Upgrading) await GlobalSupplierNotification.displaySupplierUpgradingMsg();
    else await GlobalSupplierNotification.displaySupplierOffLineMsg();
  }

  public setState(quotesupplierInfo: QuoteSupplier) {
    if (
      this._online !== quotesupplierInfo.online ||
      (!this._online && this._offlineCause !== quotesupplierInfo.offlineCause)
    ) {
      this._offlineCause = quotesupplierInfo.offlineCause;
      this._online = quotesupplierInfo.online;
      this._quoteSupplier = quotesupplierInfo;
      this._date = new Date();

      // The one reliable "we are actually back" signal - clear here rather than leaving it to the
      // boot loader's own success/bootedFromCache branch, which a transient undefined, a
      // sharedExecute join, or a mid-outage live result could all skip past without ever resetting
      // it (round 4 review).
      if (quotesupplierInfo.online) this._offlineNoticeShown = false;

      this._events.forEach(x => x(this));
    }
  }
}

async function displaySupplierOffUpgrade() {
  await information(LangSupplierServices.supplierOfflineUpgradingMsg(), LangSupplierServices.supplierOfflineTitle());
}

async function displaySupplierOffLineMsg() {
  await information(LangSupplierServices.supplierOfflineMsg(), LangSupplierServices.supplierOfflineTitle());
}

export async function displayFrameConfigLoadErrorMsg() {
  // @SUPOFF-BOOT-006: a frame-config load fault is not the supplier being offline - it must never
  // carry the "Supplier OFFLINE" title, which this used to share with the genuine offline dialogs.
  await information(
    LangSupplierServices.supplierFrameConfigOfflineMsg(),
    LangSupplierServices.frameConfigurationTitle()
  );
}

let _displaySupplierTACNotApprovedMsg = false;

export async function displaySupplierTACNotApprovedMsg(supplier: QuoteSupplier, force?: boolean) {
  if (!force && _displaySupplierTACNotApprovedMsg) return;
  _displaySupplierTACNotApprovedMsg = true;
  await information(LangSupplierServices.tacApprovalNotSetMsg({ supplier: supplier.description }));
  // eslint-disable-next-line consistent-return
  return false;
}
